import React, { useState } from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';

import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';

import api from '../../services/api';
import { Genre, WatchProvider } from '../../types/types';
import GenreItem from '../GenreItem';
import WatchProviderComponent from '../WatchProvider';

interface PropTypes {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    genres?: boolean;
    watchProviders?: boolean;
  };
  onSaveFilters: ({
    watchProvidersIds,
    genresIds
  }: {
    watchProvidersIds?: string[];
    genresIds?: number[];
  }) => void;
}

function FiltersModal({ isOpen, onClose, filters, onSaveFilters }: PropTypes) {
  const t = useTranslations();

  const [selectedWatchProviders, setSelectedWatchProviders] = useState<
    WatchProvider[]
  >([]);
  const [selectedGenres, setSelectedGenres] = useState<Genre[]>([]);

  const watchProvidersQuery = useQuery<{
    data: WatchProvider[];
  }>('me-watch-providers', async () => {
    const response = await api.get('/v1/watch-providers');

    return response.data;
  });

  const genresQuery = useQuery<{
    data: {
      id: number;
      name: string;
    }[];
  }>('genres', async () => {
    const response = await api.get('/v1/genres');

    return response.data;
  });

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="flex justify-center items-center fixed inset-0 bg-[rgba(0,0,0,0.7)] bg-opacity-25 transition-opacity z-10 px-2">
          <Dialog.Content className="w-full md:w-[500px] bg-gray-600 rounded-md p-4">
            <div className="flex flex-row justify-between items-center">
              <span className="text-white font-bold text-2xl">
                {t('modals.filters.title')}
              </span>
              <button className="text-white" onClick={onClose}>
                <MdClose size="28" />
              </button>
            </div>

            <div className="flex flex-col mt-4">
              {filters.watchProviders && (
                <div className="flex flex-col mb-4">
                  <span className="text-white">
                    {t('modals.filters.watch_providers_filter')}
                  </span>
                  <div className="gap-2 filters-modal-grid-watch-providers mt-2">
                    {watchProvidersQuery.data?.data.map(watchProvider => {
                      return (
                        <WatchProviderComponent
                          key={watchProvider._id}
                          data={watchProvider}
                          isSelected={
                            !!selectedWatchProviders.find(
                              selectedWatchProvider =>
                                selectedWatchProvider._id === watchProvider._id
                            )
                          }
                          isSelectable
                          onSelect={() => {
                            setSelectedWatchProviders(prevState => [
                              ...prevState,
                              watchProvider
                            ]);
                          }}
                          onUnselect={() => {
                            setSelectedWatchProviders(prevState =>
                              prevState.filter(
                                selectedWatchProvider =>
                                  selectedWatchProvider._id !==
                                  watchProvider._id
                              )
                            );
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {filters.genres && (
                <div className="flex flex-col mb-4">
                  <span className="text-white">
                    {t('modals.filters.genres_filter')}
                  </span>
                  <div className="flex flex-row flex-wrap gap-2 mt-2">
                    {genresQuery.data?.data.map(genre => {
                      const isSelected = !!selectedGenres.find(
                        selectedGenre => selectedGenre.id === genre.id
                      );

                      return (
                        <div key={genre.id} className="flex flex-1 relative">
                          {isSelected && (
                            <div className="flex flex-row absolute w-full h-full top-0 left-0 bottom-0 right-0 rounded-md bg-[rgba(229,9,20,0.7)] justify-center items-center">
                              <button
                                className="flex flex-1 justify-center items-center"
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedGenres(prevState =>
                                      prevState.filter(
                                        selectedGenre =>
                                          selectedGenre.id !== genre.id
                                      )
                                    );
                                  }
                                }}
                              >
                                <MdCheck size="28" />
                              </button>
                            </div>
                          )}
                          <GenreItem
                            onClick={() => {
                              if (!isSelected) {
                                setSelectedGenres(prevState => [
                                  ...prevState,
                                  genre
                                ]);
                              }
                            }}
                            key={genre.id}
                            data={genre}
                            className="flex w-auto md:w-auto h-auto md:h-auto flex-1 py-2 text-sm cursor-pointer"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-2">
                <button
                  className="w-full mt-2 text-white bg-red-600 hover:bg-red-500 h-[40px] rounded-md font-bold px-4"
                  onClick={() => {
                    onSaveFilters({
                      genresIds: selectedGenres.map(genre => genre.id),
                      watchProvidersIds: selectedWatchProviders.map(
                        watchProvider => watchProvider._id
                      )
                    });
                    onClose();
                  }}
                >
                  {t('modals.filters.save_filters')}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default FiltersModal;
